import { CartAddEvent } from '@theme/events';

/**
 * Blog/article sticky add-to-cart bar.
 *  - Reveals after the reader scrolls down a little.
 *  - Adds the resolved product to cart and opens the cart drawer.
 * The product is resolved server-side in blog-sticky-buy-bar.liquid.
 */
(function () {
  const BAR_SELECTOR = '[data-blog-sticky-bar]';

  function getCartSectionIds() {
    return Array.from(document.querySelectorAll('cart-items-component'))
      .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : null))
      .filter(Boolean);
  }

  /**
   * @param {number} variantId
   * @param {number} [sellingPlanId]
   */
  async function addToCart(variantId, sellingPlanId) {
    const sections = getCartSectionIds().join(',');
    /** @type {Record<string, unknown>} */
    const item = { id: variantId, quantity: 1 };
    if (sellingPlanId) item.selling_plan = sellingPlanId;

    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [item], sections, sections_url: window.location.pathname }),
    });

    const data = await res.json();
    if (!res.ok || data.status) {
      throw new Error(data.description || data.message || 'Could not add this item to your cart.');
    }
    return data;
  }

  /**
   * Delegated handler so ANY [data-blog-sticky-add] button works — the
   * sticky bar, the in-article shop CTA (recipe + journal), etc.
   * @param {HTMLButtonElement} cta
   */
  async function handleAddClick(cta) {
    const variantId = Number(cta.getAttribute('data-variant-id'));
    if (!variantId) return;
    const sellingPlanId = Number(cta.getAttribute('data-selling-plan-id')) || undefined;

    cta.disabled = true;
    cta.setAttribute('aria-busy', 'true');
    try {
      const response = await addToCart(variantId, sellingPlanId);
      document.dispatchEvent(
        new CartAddEvent(response, 'blog-shop-cta', {
          // Reuse a source the cart drawer already treats as "auto-open".
          source: 'cart-drawer-add-ons',
          itemCount: response.item_count ?? 0,
          variantId: String(variantId),
          sections: response.sections,
        })
      );
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(err);
      if (err instanceof Error && err.message) cta.setAttribute('aria-label', err.message);
    } finally {
      cta.disabled = false;
      cta.removeAttribute('aria-busy');
    }
  }

  /** @param {HTMLElement} bar */
  function initBarReveal(bar) {
    if (bar.dataset.blogStickyInit === '1') return;
    bar.dataset.blogStickyInit = '1';

    const revealAfter = () => Math.min(480, window.innerHeight * 0.6);

    const update = () => {
      const scrollY = window.scrollY || window.pageYOffset || 0;
      // Hide near the very bottom so it doesn't cover the footer CTA.
      const nearBottom =
        window.innerHeight + scrollY >= document.documentElement.scrollHeight - 160;
      const show = scrollY > revealAfter() && !nearBottom;
      bar.classList.toggle('is-visible', show);
      bar.setAttribute('aria-hidden', show ? 'false' : 'true');
      if (show) {
        document.documentElement.style.setProperty(
          '--blog-sticky-bar-height',
          `${bar.offsetHeight}px`
        );
      } else {
        document.documentElement.style.removeProperty('--blog-sticky-bar-height');
      }
    };

    update();
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update, { passive: true });
  }

  function init() {
    // One delegated listener covers every current and future add button.
    document.addEventListener('click', (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      const cta = target.closest('[data-blog-sticky-add]');
      if (cta instanceof HTMLButtonElement) handleAddClick(cta);
    });

    document.querySelectorAll(BAR_SELECTOR).forEach((bar) => {
      if (bar instanceof HTMLElement) initBarReveal(bar);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
